import {ErrorCode} from '@sollapay/enums';
import {HttpClientError} from '@sollapay/http-client';

/**
 * Extract error message from various error types
 * Handles Error objects, HttpClientError, and unknown error types
 *
 * @param error - The error to extract message from
 * @param fallback - Fallback message if error message cannot be extracted
 * @returns Error message string
 */
export function getErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  if (error instanceof HttpClientError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return fallback;
}

/**
 * Check if error is an HttpClientError
 */
export function isHttpClientError(error: unknown): error is HttpClientError {
  return error instanceof HttpClientError;
}

/**
 * Get status code from HttpClientError
 * Returns undefined for non-HTTP errors
 */
export function getErrorStatusCode(error: unknown): number | undefined {
  if (isHttpClientError(error)) {
    return error.statusCode;
  }
  return undefined;
}

/**
 * Get error code from HttpClientError
 * Returns undefined for non-HTTP errors
 */
export function getErrorCode(error: unknown): string | undefined {
  if (isHttpClientError(error)) {
    return error.code;
  }
  return undefined;
}

/**
 * Get error details from HttpClientError
 * Returns undefined for non-HTTP errors
 */
export function getErrorDetails(error: unknown): unknown | undefined {
  if (isHttpClientError(error)) {
    return error.details;
  }
  return undefined;
}

interface ZodFieldIssue {
  field?: string;
  code?: string;
  message?: string;
}

/**
 * Returns translated error messages as an array for display in form Alerts.
 *
 * - VALIDATION_ERROR with Zod details → one message per field: "{fieldLabel} {zodErrorReason}"
 * - Other ErrorCode values → single-item array with common:errors.{CODE} translation
 * - Fallback → single-item array with raw error message or common:errors.default
 */
export function getTranslatedErrorMessages(
  error: unknown,
  t: (key: string, options?: Record<string, string>) => string
): string[] {
  const code = getErrorCode(error);

  if (code === ErrorCode.BUYER_VALIDATION_ERROR) {
    const details = getErrorDetails(error);
    if (Array.isArray(details) && details.length > 0) {
      const messages = (details as {code: string; params?: Record<string, string | number>}[]).map(
        issue =>
          t(`common:buyerValidation.${issue.code}`, {
            defaultValue: issue.code,

            ...(issue.params ?? {})
          })
      );
      if (messages.length > 0) return messages;
    }
  }

  if (code === ErrorCode.VALIDATION_ERROR) {
    const details = getErrorDetails(error);
    if (Array.isArray(details) && details.length > 0) {
      const messages = (details as ZodFieldIssue[])
        .map(issue => {
          const fieldLabel = issue.field
            ? t(`common:fields.${issue.field}.label`, {defaultValue: issue.field})
            : undefined;
          const reason = issue.code
            ? t(`common:zodErrors.${issue.code}`, {defaultValue: issue.message ?? 'is invalid'})
            : (issue.message ?? 'is invalid');
          return fieldLabel ? `${fieldLabel} ${reason}` : reason;
        })
        .filter(Boolean);
      if (messages.length > 0) return messages;
    }
  }

  if (code) {
    const translated = t(`common:errors.${code}`, {
      ...toInterpolationParams(getErrorDetails(error)),
      defaultValue: ''
    });
    if (translated) return [translated];
  }

  return [
    getErrorMessage(
      error,
      t('common:errors.default', {defaultValue: 'Something went wrong. Please try again.'})
    )
  ];
}

/**
 * Returns a single translated error message for display in form Alerts.
 * Multiple validation errors are joined with '. '.
 */
export function getTranslatedErrorMessage(
  error: unknown,
  t: (key: string, options?: Record<string, string>) => string
): string {
  return getTranslatedErrorMessages(error, t).join('. ');
}

/** String-valued error details double as i18n interpolation params (e.g. `{existingName}`). */
function toInterpolationParams(details: unknown): Record<string, string> {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};
  return Object.fromEntries(
    Object.entries(details).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string'
    )
  );
}
